import React from 'react';
import { Link } from 'react-router-dom';
import useScrollAnimation from '../hooks/useScrollAnimation';
import ContactForm from '../components/ContactForm';
import siteContent from '../content';

const { hero, tips, process, marketing, whyUs, valuation, contact } = siteContent.pages.sellers.sections;

const Sellers: React.FC = () => {
  const tipsSection = useScrollAnimation({ threshold: 0 });
  const processSection = useScrollAnimation({ threshold: 0 });
  const marketingSection = useScrollAnimation({ threshold: 0 });
  const whySection = useScrollAnimation({ threshold: 0 });
  const contactSection = useScrollAnimation({ threshold: 0 });

  return (
    <>
      <title>Seller Services | Kline Real Estate Works</title>
      <meta
        name="description"
        content="Sell your home for top dollar with Kline Real Estate Works. Professional marketing, expert pricing, and dedicated service in Royersford, PA."
      />
      <meta property="og:title" content="Seller Services | Kline Real Estate Works" />
      <meta property="og:description" content="Sell your home for top dollar with Kline Real Estate Works. Professional marketing, expert pricing, and dedicated service in Royersford, PA." />
      <meta property="og:image" content="https://keithklinegroup.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://keithklinegroup.com/og-image.jpg" />

      {/* Hero Section */}
      <section
        className="hero hero-with-image"
        data-cms-page="sellers"
        data-cms-section="hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 43, 92, 0.75), rgba(0, 43, 92, 0.85)), url('${hero.image}')`
        }}
        data-cms-field="image"
        data-cms-type="image"
      >
        <div className="hero-content">
          <p className="hero-subtitle" data-cms-field="subtitle">{hero.subtitle}</p>
          <h1 data-cms-field="heading">{hero.heading}</h1>
          <p data-cms-field="description">{hero.description}</p>
          <Link to={hero.ctaLink} className="btn btn-primary btn-lg" data-cms-field="ctaText" data-cms-link-field="ctaLink">{hero.ctaText}</Link>
        </div>
      </section>

      {/* Selling Tips Section */}
      <section className="section" data-cms-page="sellers" data-cms-section="tips">
        <div className="container">
          <div
            ref={tipsSection.ref}
            className={`scroll-fade-up ${tipsSection.isVisible ? 'visible' : ''}`}
          >
            <div className="two-column">
              <div>
                <h2 data-cms-field="heading">{tips.heading}</h2>
                <div data-cms-list="paragraphs">
                  {tips.paragraphs.map((paragraph, index) => (
                    <p
                      key={index}
                      style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--color-gray-700)' }}
                      data-cms-field="paragraphs"
                      data-cms-index={index}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
              <div>
                <div className="card" style={{ padding: '2rem', backgroundColor: 'var(--color-navy)', color: 'white' }}>
                  <h3 style={{ color: 'var(--color-gold)', marginBottom: '1.5rem' }} data-cms-field="benefitsTitle">{tips.benefitsTitle}</h3>
                  <ul style={{ listStyle: 'none', padding: 0 }} data-cms-list="benefits">
                    {tips.benefits.map((benefit, index) => (
                      <li
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          marginBottom: '1rem'
                        }}
                        data-cms-index={index}
                      >
                        <span className="material-icons" style={{ color: 'var(--color-gold)', fontSize: '1.25rem', flexShrink: 0 }}>
                          check_circle
                        </span>
                        <span style={{ fontSize: '0.95rem' }} data-cms-field="benefits" data-cms-index={index}>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section" data-cms-page="sellers" data-cms-section="process">
        <div className="container">
          <div
            ref={processSection.ref}
            className={`scroll-fade-up ${processSection.isVisible ? 'visible' : ''}`}
          >
            <div className="section-header">
              <h2 data-cms-field="heading">{process.heading}</h2>
              <p data-cms-field="subheading">{process.subheading}</p>
            </div>
            <div className="services-grid" data-cms-list="steps">
              {process.steps.map((step, index) => (
                <div
                  key={index}
                  className="service-card"
                  data-cms-index={index}
                >
                  <span className="material-icons service-icon" data-cms-field="steps" data-cms-index={index} data-cms-item-field="icon" data-cms-field-type="icon">{step.icon}</span>
                  <h3 data-cms-field="steps" data-cms-index={index} data-cms-item-field="title">{step.title}</h3>
                  <p data-cms-field="steps" data-cms-index={index} data-cms-item-field="description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Section */}
      <section className="section section-bg" data-cms-page="sellers" data-cms-section="marketing">
        <div className="container">
          <div
            ref={marketingSection.ref}
            className={`scroll-fade-up ${marketingSection.isVisible ? 'visible' : ''}`}
          >
            <div className="section-header">
              <h2 data-cms-field="heading">{marketing.heading}</h2>
              <p data-cms-field="subheading">{marketing.subheading}</p>
            </div>
            <div className="grid grid-2" style={{ maxWidth: '900px', margin: '0 auto' }} data-cms-list="items">
              {marketing.items.map((item, index) => (
                <div
                  key={index}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    padding: '1.5rem'
                  }}
                  data-cms-index={index}
                >
                  <span className="material-icons" style={{ color: 'var(--color-gold)', fontSize: '2rem' }} data-cms-field="items" data-cms-index={index} data-cms-item-field="icon" data-cms-field-type="icon">
                    {item.icon}
                  </span>
                  <div>
                    <h4 style={{ marginBottom: '0.25rem' }} data-cms-field="items" data-cms-index={index} data-cms-item-field="title">{item.title}</h4>
                    <p style={{ marginBottom: 0, color: 'var(--color-gray-600)', fontSize: '0.95rem' }} data-cms-field="items" data-cms-index={index} data-cms-item-field="description">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section" data-cms-page="sellers" data-cms-section="whyUs">
        <div className="container">
          <div
            ref={whySection.ref}
            className={`scroll-fade-up ${whySection.isVisible ? 'visible' : ''}`}
          >
            <div className="two-column">
              <div>
                <h2 data-cms-field="heading">{whyUs.heading}</h2>
                <p data-cms-field="description">{whyUs.description}</p>
                <ul style={{ listStyle: 'none', padding: 0 }} data-cms-list="benefits">
                  {whyUs.benefits.map((benefit, index) => (
                    <li
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        marginBottom: '1rem'
                      }}
                      data-cms-index={index}
                    >
                      <span className="material-icons" style={{ color: 'var(--color-gold)', fontSize: '1.5rem' }}>
                        check_circle
                      </span>
                      <span data-cms-field="benefits" data-cms-index={index}>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div data-cms-page="sellers" data-cms-section="valuation">
                <div className="card" style={{ padding: '2rem', backgroundColor: 'var(--color-navy)', color: 'white' }}>
                  <h3 style={{ color: 'var(--color-gold)', marginBottom: '1rem', textAlign: 'center' }} data-cms-field="heading">{valuation.heading}</h3>
                  <p style={{ marginBottom: '1.5rem' }} data-cms-field="description">
                    {valuation.description}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1.5rem' }} data-cms-list="features">
                    {valuation.features.map((feature, index) => (
                      <li
                        key={index}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
                        data-cms-index={index}
                      >
                        <span className="material-icons" style={{ fontSize: '1rem' }}>check</span>
                        <span data-cms-field="features" data-cms-index={index}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div style={{ textAlign: 'center' }}>
                    <Link to={valuation.ctaLink} className="btn btn-primary" data-cms-field="ctaText" data-cms-link-field="ctaLink">{valuation.ctaText}</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section section-bg">
        <div className="container">
          <div
            ref={contactSection.ref}
            className={`scroll-scale ${contactSection.isVisible ? 'visible' : ''}`}
            style={{ maxWidth: '700px', margin: '0 auto' }}
          >
            <ContactForm
              title={contact.title}
              description={contact.description}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Sellers;
